import { create } from 'zustand';
import type { Id } from '../lib/types';

// Per-device, never synced. See plan/01 D8.
// Kept tiny (~1 KB) per plan/02 §Storage layout.

const KEY = 'aamusankari:device';

export interface DeviceState {
  activeProfileId: Id | null;
  pin: string | null;
  syncCode: string | null;
  lastSeenVersion: string | null;
  // Transient session flag — not persisted. Set by the sync engine when
  // boot-time auth fails (token expired or family record removed) so
  // the UI can render a "yhteys katkesi" banner on the next render.
  // Cleared when the user re-enters a code or explicitly dismisses.
  syncAuthLost: boolean;
  setActiveProfileId: (id: Id | null) => void;
  setPin: (pin: string | null) => void;
  setSyncCode: (code: string | null) => void;
  setLastSeenVersion: (version: string | null) => void;
  setSyncAuthLost: (value: boolean) => void;
}

interface PersistedShape {
  activeProfileId: Id | null;
  pin: string | null;
  syncCode: string | null;
  lastSeenVersion: string | null;
}

const defaults: PersistedShape = {
  activeProfileId: null,
  pin: null,
  syncCode: null,
  lastSeenVersion: null,
};

function readFromStorage(): PersistedShape {
  if (typeof localStorage === 'undefined') return defaults;
  const raw = localStorage.getItem(KEY);
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function writeToStorage(state: PersistedShape): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (err) {
    // Quota exceeded, private-mode lockdown, etc. Device state is small
    // (~1 KB) so this should never realistically happen; surface for
    // diagnosis but don't crash the setter.
    console.warn('writeToStorage failed', err);
  }
}

export const useDeviceStore = create<DeviceState>()((set, get) => {
  const persistField = <K extends keyof PersistedShape>(key: K, value: PersistedShape[K]) => {
    set({ [key]: value } as Pick<DeviceState, K>);
    const { activeProfileId, pin, syncCode, lastSeenVersion } = get();
    writeToStorage({ activeProfileId, pin, syncCode, lastSeenVersion });
  };

  return {
    ...defaults,
    syncAuthLost: false,
    setActiveProfileId: (id) => persistField('activeProfileId', id),
    setPin: (pin) => persistField('pin', pin),
    setSyncCode: (code) => persistField('syncCode', code),
    setLastSeenVersion: (version) => persistField('lastSeenVersion', version),
    // Transient — never persisted, never read from storage.
    setSyncAuthLost: (value) => set({ syncAuthLost: value }),
  };
});

// Hydrate the store from localStorage. Call once at app boot.
export function hydrateDeviceStore(): void {
  useDeviceStore.setState(readFromStorage());
}

// For tests: clear both the in-memory store and localStorage.
export function resetDeviceStoreForTests(): void {
  if (typeof localStorage !== 'undefined') localStorage.removeItem(KEY);
  useDeviceStore.setState({ ...defaults, syncAuthLost: false });
}
